import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
var _excluded = ["id", "children", "width", "boxShadow", "isSticky", "isStacked", "header"];
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { View } from '@instructure/ui-view';
var CellComponent = function CellComponent(_ref) {
  var id = _ref.id,
    children = _ref.children,
    width = _ref.width,
    boxShadow = _ref.boxShadow,
    isSticky = _ref.isSticky,
    isStacked = _ref.isStacked,
    header = _ref.header,
    props = _objectWithoutProperties(_ref, _excluded);
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    focus = _useState2[0],
    setFocus = _useState2[1];
  var cellRef = useRef(null);
  var handleBlur = useCallback(function (event) {
    var _cellRef$current;
    var relatedTarget = event.relatedTarget;
    if (relatedTarget && (_cellRef$current = cellRef.current) !== null && _cellRef$current !== void 0 && _cellRef$current.contains(relatedTarget)) {
      return;
    }
    setFocus(false);
  }, []);
  var handleFocus = useCallback(function () {
    return setFocus(true);
  }, []);
  var elementRef = useCallback(function (el) {
    if (el instanceof HTMLElement) {
      cellRef.current = el;
    }
  }, []);
  var themeOverride = useMemo(function () {
    return function (_componentTheme, currentTheme) {
      return {
        shadowAbove: boxShadow !== null && boxShadow !== void 0 ? boxShadow : "2px 0 0 0 ".concat(currentTheme.colors.contrasts.grey1214)
      };
    };
  }, [boxShadow]);
  return /*#__PURE__*/React.createElement(View, Object.assign({
    id: id,
    width: width,
    height: "inherit",
    borderWidth: "0 0 small 0",
    overflowX: "auto",
    as: isStacked ? 'div' : 'td',
    role: isStacked ? 'cell' : void 0,
    position: isSticky ? 'sticky' : 'static',
    stacking: "above",
    insetInlineStart: isSticky ? '0' : void 0,
    background: isSticky && !props.background ? 'primary' : props.background,
    focusPosition: "inset",
    elementRef: elementRef,
    onFocus: handleFocus,
    onBlur: handleBlur,
    themeOverride: themeOverride
  }, props), header ? typeof header === 'function' ? header() : header : null, typeof children === 'function' ? children(focus) : children);
};

// Custom comparison function for React.memo
// We need to compare most props, but be smart about function props
var arePropsEqual = function arePropsEqual(prevProps, nextProps) {
  // If the id changed, definitely re-render
  if (prevProps.id !== nextProps.id) return false;

  // Check data attributes that affect keyboard navigation
  if (prevProps['data-row-index'] !== nextProps['data-row-index']) return false;
  if (prevProps['data-col-key'] !== nextProps['data-col-key']) return false;
  if (prevProps['data-cell-id'] !== nextProps['data-cell-id']) return false;

  // Check visual props
  if (prevProps.width !== nextProps.width) return false;
  if (prevProps.boxShadow !== nextProps.boxShadow) return false;
  if (prevProps.isSticky !== nextProps.isSticky) return false;
  if (prevProps.isStacked !== nextProps.isStacked) return false;

  // Check other props that could change
  if (prevProps.tabIndex !== nextProps.tabIndex) return false;

  // onKeyDown should be stable (useCallback), but check just in case
  if (prevProps.onKeyDown !== nextProps.onKeyDown) return false;

  // Compare children by reference - function children must be stabilized with useCallback
  // by the caller if re-renders should be avoided
  if (prevProps.children !== nextProps.children) return false;

  // For function children, skip comparison - they're always new references but produce same output
  // when all other props are the same

  return true;
};
export var Cell = /*#__PURE__*/React.memo(CellComponent, arePropsEqual);
Cell.displayName = 'Cell';
export default Cell;